import React, { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { FaBars, FaXmark } from 'react-icons/fa6';
import LanguageSelector from './LanguageSelector';
import UserIndicator from './UserIndicator';
import { useLogin } from '../../hooks/useLogin';
import { useVerificationWall } from '../../hooks/useVerificationWall';
import { useStaticPages, useAccountInfo } from '../../api/queryHooks/account';
import { TOKEN_KEY, GUEST_TOKEN_KEY } from '../../api/queryHooks/user';
import CartIndicator from '../order/cart-indicator/CartIndicator';
import ChatIndicator from '../chat/ChatIndicator';
import ReminderBell from '../reminders/ReminderBell';
import { isStaff } from '../../auth/roles';
import './TopNav.scss';

const TopNav = () => {
  const { t } = useTranslation();
  const { userData, isLoggedIn, isGuest } = useLogin();
  // Account-only widgets (chat, reminders) stay away from a pending user, as from a guest.
  const { pending } = useVerificationWall();
  const accountWidgets = isLoggedIn && !isGuest && !pending;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { data: staticPages } = useStaticPages();
  // Without a designated homepage "/" only redirects (albums or login), so
  // there is no Home to link to. The public index lists the homepage page
  // regardless of its menu/footer placement flags.
  const hasHomepage = staticPages?.some((page) => page.homepage_page) ?? false;
  const { data: accountInfo } = useAccountInfo();
  const logoUrl = accountInfo?.setting?.logo_url ?? null;
  const shopName = accountInfo?.display_name ?? accountInfo?.name ?? 'Foto2Me';
  const navigate = useNavigate();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const handleLoginClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    closeMenu();
    // If user is a guest, save their token for transfer after login
    if (isGuest) {
      const currentToken = localStorage.getItem(TOKEN_KEY);
      if (currentToken) {
        localStorage.setItem(GUEST_TOKEN_KEY, currentToken);
      }
    }
    navigate('/login');
  };

  return (
    <div className="top-nav">
      <nav className="navbar" role="navigation" aria-label="main navigation">
        <div className="navbar-brand">
          <span
            role="button"
            className="navbar-burger"
            aria-label="menu"
            aria-expanded={isMenuOpen}
            onClick={toggleMenu}
            tabIndex={0}
            onKeyPress={(e) => e.key === 'Enter' && toggleMenu()}
          >
            {isMenuOpen ? <FaXmark size={24} /> : <FaBars size={24} />}
          </span>

          <Link to="/" className="navbar-item navbar-logo" onClick={closeMenu}>
            {logoUrl
              ? <img src={logoUrl} alt={shopName} className="brand-logo" />
              : <span className="brand-name">{shopName}</span>}
          </Link>

          {/* Chat, Cart and User icons always visible on mobile */}
          <div className="navbar-brand-end">
            { accountWidgets && (
              <div className="navbar-item navbar-item-mobile-visible">
                <ChatIndicator />
              </div>
            )}
            { accountWidgets && (
              <div className="navbar-item navbar-item-mobile-visible">
                <ReminderBell />
              </div>
            )}
            { isLoggedIn && (
              <div className="navbar-item navbar-item-mobile-visible">
                <CartIndicator />
              </div>
            )}
            {/* The compact mobile pill below shares its row with the burger and
                logo, so it keeps the short label: the full "Log in / Register"
                (#174) overflows the bar at 320px in de and hu. The desktop nav
                further down uses the full nav.login label. */}
            <div className="navbar-item navbar-item-mobile-visible">
              { isLoggedIn && !isGuest && userData ? (
                <UserIndicator userData={userData} />
              ) : (
                <a href="/login" className="login-button-mobile" onClick={handleLoginClick}>
                  {t('nav.login_short')}
                </a>
              )}
            </div>
          </div>
        </div>
        <div className={`navbar-menu ${isMenuOpen ? 'is-active' : ''}`}>
          <div className="navbar-start">
            {hasHomepage && (
              <NavLink
                to="/"
                className={({ isActive }) => `navbar-item ${isActive ? 'active' : ''}`}
                onClick={closeMenu}
              >
                {t('nav.home')}
              </NavLink>
            )}
            <NavLink
              to="/albums"
              className={({ isActive }) => `navbar-item ${isActive ? 'active' : ''}`}
              onClick={closeMenu}
            >
              {t('nav.albums')}
            </NavLink>
            {staticPages?.filter((page) => page.show_in_top_menu !== false).map((page) => (
              <NavLink
                key={page.id}
                to={`/page/${page.slug}`}
                className={({ isActive }) => `navbar-item ${isActive ? 'active' : ''}`}
                onClick={closeMenu}
              >
                {page.title}
              </NavLink>
            ))}
          </div>

          <div className="navbar-end">
            { isLoggedIn && !isGuest && isStaff(userData?.roles) && (
              <NavLink
                to="/admin"
                className={({ isActive }) => `navbar-item ${isActive ? 'active' : ''}`}
                onClick={closeMenu}
              >
                {t('admin.nav.admin')}
              </NavLink>
            )}
            <div className="navbar-item navbar-item-desktop-only">
              <ChatIndicator />
            </div>
            <div className="navbar-item navbar-item-desktop-only">
              <ReminderBell />
            </div>
            <div className="navbar-item navbar-item-desktop-only">
              <LanguageSelector />
            </div>
            <div className="navbar-item navbar-item-desktop-only">
              { isLoggedIn && <CartIndicator /> }
            </div>
            <div className="navbar-item navbar-item-desktop-only">
              { isLoggedIn && !isGuest && userData ? (
                <UserIndicator userData={userData} />
              ) : (
                <a href="/login" className="login-button" onClick={handleLoginClick}>
                  {t('nav.login')}
                </a>
              )}
            </div>
          </div>
        </div>
      </nav>
    </div>
  );
};

export default TopNav;
